import { FC, ReactElement, memo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { PagesQueryKeys, useDeletePagesMutation, useGetOneByIdDossierPagesQuery } from '@/modules/Pages';

import { PagesDossierView } from './PagesDossierView';

interface Props {
  pageId: string;
  siteId: string;
}

export const PagesDossierContainer: FC<Props> = memo(({ pageId, siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: pageData,
    error: pageDataError,
    isLoading: isPageDataFetching,
    isError: isPageDataError
  } = useGetOneByIdDossierPagesQuery(
    pageId,
    siteId
  )({
    enabled: !!pageId && !!siteId
  });

  const { mutate: deletePageMutation, isLoading: isPageDeleteLoading } = useDeletePagesMutation(
    pageId,
    siteId
  )({
    onSuccess: async (): Promise<void> => {
      appMessageApi.success('Сторінку сайту видалено успішно!');

      await queryClient.refetchQueries([PagesQueryKeys.GetAll], { exact: false });

      navigate(AppRoutes.Pages.replace(':siteId', siteId));
    },
    onError: (err) => {
      messageOnError(err, appMessageApi);
    }
  });

  const handleDeletePage = useCallback(() => {
    deletePageMutation();
  }, []);

  return (
    <FallbackedLayout isLoading={ isPageDataFetching } error={ pageDataError } isError={ isPageDataError } data={ pageData }>
      { (page): ReactElement => (
        <PagesDossierView
          page={{
            ...page,
            structureNode: {
              ...page.structureNode,
              pages: page.structureNode.pages.filter((p) => p.id !== page.id)
            }
          }}
          isDeleteLoading={ isPageDeleteLoading }
          disableControlButtons={ isPageDeleteLoading }
          handleDeletePage={ handleDeletePage }
        />
      ) }
    </FallbackedLayout>
  );
});
